import * as React from "react";
import { useMergeRefs, FloatingOverlay, FloatingFocusManager, FloatingPortal } from "@floating-ui/react";
import clsx from "clsx";
import { CloseButton } from "../../close-button/close-button.mjs";
import "debounce";
import "tabbable";
import { useIsMounted } from "../../helpers/helper/hooks/use-is-mounted.mjs";
import { useResponsiveProps } from "../../helpers/helper/hooks/use-responsive-props.mjs";
import { useLabels } from "../../helpers/label-provider/use-labels.mjs";
import { Spinner } from "../../spinner/spinner.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
import { VisibilityProvider } from "../../helpers/visibility-provider/visibility-provider.mjs";
import { ModalContent } from "../modal-content/modal-content.mjs";
import { ModalCover } from "../modal-cover/modal-cover.mjs";
import { ModalFooter } from "../modal-footer/modal-footer.mjs";
import { ModalHeader } from "../modal-header/modal-header.mjs";
import { ModalContext } from "../modal-provider/modal-provider.mjs";
import { useModal } from "../use-modal.mjs";
const Modal = (props) => {
  const labels = useLabels();
  const context = React.useContext(ModalContext);
  const { responsive, ...rest } = props;
  return responsive ? /* @__PURE__ */ React.createElement(ModalResponsive, { ...rest, responsive, labels, context }) : /* @__PURE__ */ React.createElement(ModalInner, { ...rest, labels, context });
};
const ModalResponsive = (props) => {
  const { responsive, ...rest } = props;
  const responsiveProps = useResponsiveProps({
    responsive
  });
  return /* @__PURE__ */ React.createElement(ModalInner, { ...rest, ...responsiveProps });
};
const ModalInner = (props) => {
  var _a;
  const {
    isClosable = true,
    isOpen,
    initialFocusedElementId,
    onClose,
    size,
    verticalAlign,
    className,
    sidebar,
    isFullHeight,
    isFullScreen,
    children,
    closeButtonLabel,
    hasContentComponent,
    cover,
    isHeaderSticky,
    closeButtonTheme
  } = props;
  const isMounted = useIsMounted();
  const { portalRoot } = useTheme();
  const context = React.useContext(ModalContext);
  const modal = React.useRef(null);
  const initiallyFocused = React.useRef(null);
  const close = React.useCallback(
    (event) => {
      event.preventDefault();
      if (onClose) {
        onClose(event);
      }
      context.close(event);
    },
    [context, onClose]
  );
  const handleOpenChange = React.useCallback(
    (nextOpen, _e) => {
      if (nextOpen === false && _e) {
        close(_e);
      }
    },
    [close]
  );
  const innerModalContext = useModal({ open: isOpen, onOpenChange: handleOpenChange, isClosable });
  const modalContext = React.useMemo(() => {
    var _a2;
    if (typeof isOpen !== "undefined") {
      return innerModalContext;
    }
    return (_a2 = context.state) != null ? _a2 : innerModalContext;
  }, [context.state, innerModalContext, isOpen]);
  const isModalOpen = React.useMemo(() => {
    const contextOpen = modalContext.context.open;
    return typeof isOpen !== "undefined" ? isOpen : !!contextOpen;
  }, [modalContext.context.open, isOpen]);
  const renderHeaderOrFooter = (type) => {
    let element = null;
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === type) {
        element = child;
      }
    });
    return element;
  };
  const renderBody = () => {
    const bodyChildren = React.Children.toArray(children).filter((child) => {
      return React.isValidElement(child) && child.type !== ModalHeader && child.type !== ModalFooter;
    });
    return /* @__PURE__ */ React.createElement(ModalContent, null, bodyChildren);
  };
  const shouldBeClosable = () => {
    return !!(isClosable && context.isClosable);
  };
  const renderCloseButton = () => {
    const { isClosable: isClosable2 } = props;
    if (isClosable2 !== "backdrop") {
      return /* @__PURE__ */ React.createElement("div", { className: "modal__close-wrapper" }, /* @__PURE__ */ React.createElement(CloseButton, { className: "modal__close", onClick: close, label: closeButtonLabel, theme: closeButtonTheme, type: "modal" }));
    }
  };
  const ref = useMergeRefs([(_a = context.state) == null ? void 0 : _a.refs.setFloating, innerModalContext.refs.setFloating, modal]);
  const modalSize = size != null ? size : sidebar ? "4-col" : void 0;
  const initialFocusElement = initialFocusedElementId ? document.getElementById(initialFocusedElementId) : null;
  const getInitialFocus = (element) => {
    initiallyFocused.current = element;
    return initiallyFocused;
  };
  const content = /* @__PURE__ */ React.createElement(VisibilityProvider, { isVisible: isModalOpen }, /* @__PURE__ */ React.createElement(
    FloatingOverlay,
    {
      className: clsx(
        "modal",
        "is-".concat(modalContext.status),
        {
          ["modal--full-screen"]: isFullScreen,
          ["modal--with-sidebar-".concat(sidebar === true ? "right" : sidebar)]: sidebar
        },
        className
      ),
      lockScroll: isMounted,
      onClick: (e) => {
        e.stopPropagation();
      }
    },
    /* @__PURE__ */ React.createElement(
      FloatingFocusManager,
      {
        context: modalContext.context,
        initialFocus: initialFocusElement ? getInitialFocus(initialFocusElement) : 0,
        outsideElementsInert: true,
        guards: isMounted,
        disabled: !isMounted,
        modal: true
      },
      /* @__PURE__ */ React.createElement(
        "div",
        {
          ref,
          className: clsx("modal__inner", {
            ["modal__inner--".concat(modalSize)]: size != null ? size : sidebar,
            ["modal__inner--vertical-align-".concat(verticalAlign)]: verticalAlign,
            ["modal__inner--full-height"]: isFullHeight,
            ["modal__inner--full-screen"]: isFullScreen,
            ["modal__inner--with-sidebar-".concat(sidebar === true ? "right" : sidebar)]: sidebar,
            ["modal__inner--with-sticky-header"]: isHeaderSticky
          }),
          "aria-labelledby": props["aria-labelledby"],
          "aria-describedby": props["aria-describedby"],
          ...modalContext.getFloatingProps({
            id: context.id
          })
        },
        /* @__PURE__ */ React.createElement(React.Suspense, { fallback: /* @__PURE__ */ React.createElement(Spinner, { className: "modal__spinner" }) }, /* @__PURE__ */ React.createElement("div", { className: "modal__box" }, cover && /* @__PURE__ */ React.createElement(ModalCover, { ...cover }), /* @__PURE__ */ React.createElement("div", { className: "modal__box-inner" }, shouldBeClosable() && renderCloseButton(), !hasContentComponent && renderHeaderOrFooter(ModalHeader), !hasContentComponent && renderBody(), !hasContentComponent && renderHeaderOrFooter(ModalFooter), hasContentComponent && children)))
      )
    )
  ));
  if (!isMounted) {
    return content;
  }
  return modalContext.isMounted && /* @__PURE__ */ React.createElement(FloatingPortal, { root: portalRoot }, content);
};
export {
  Modal
};
